<script setup lang="ts">
  import { ref } from 'vue';

  import { HTTPError } from '@stuut/api-sdk/models/errors';
  import type { EmailSender } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { ConfirmDialog } from '@ui/components/confirm-dialog';
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
  } from '@ui/components/dropdown-menu';
  import { toast } from '@ui/components/sonner';

  import { stuut } from '@/api';
  import { sdk } from '@/sdk';
  import { useEmailSenders, useProfile } from '@/store';

  interface Props {
    emailSender: EmailSender;
  }

  const props = defineProps<Props>();

  const emits = defineEmits<{
    (e: 'editSignature'): void;
    (e: 'deleted'): void;
  }>();

  const profileStore = useProfile();
  const emailSendersStore = useEmailSenders();

  const confirmDeleteDialogOpen = ref(false);

  const isLoading = ref(false);

  const setDefaultSender = () => {
    if (isLoading.value) return;
    isLoading.value = true;

    stuut.profile
      .update('', { data: { default_email_sender: props.emailSender.id } }, profileStore.profile)
      .catch((error) => {
        toast.error(error.clientMessage || 'An error occurred');
      })
      .finally(() => (isLoading.value = false));
  };

  const setShared = () => {
    if (isLoading.value || props.emailSender.user_id !== profileStore.profile.id) {
      return;
    }

    isLoading.value = true;
    stuut.emailSenders
      .update(
        props.emailSender.id,
        { data: { is_shared: !props.emailSender.is_shared } },
        props.emailSender,
      )
      .catch((error) => {
        toast.error(error.clientMessage || 'An error occurred');
      })
      .finally(() => (isLoading.value = false));
  };

  const toggleCentralizedInbox = async () => {
    if (isLoading.value || props.emailSender.user_id !== profileStore.profile.id) {
      return;
    }

    const newState = !props.emailSender.is_centralized_inbox;
    isLoading.value = true;
    const successToast = () => {
      toast.success(
        `${props.emailSender.email} ${newState ? 'added to' : 'removed from'} centralized inbox.`,
      );
    };

    try {
      await sdk.emailThreads.toggleCentralizedInbox({
        body: { isCentralizedInbox: newState },
        emailSenderId: props.emailSender.id,
      });
      emailSendersStore.patchEmailSender(props.emailSender.id, {
        is_centralized_inbox: newState,
      });
      successToast();
    } catch (error: unknown) {
      if (error instanceof HTTPError && error.statusCode === 204) {
        emailSendersStore.patchEmailSender(props.emailSender.id, {
          is_centralized_inbox: newState,
        });
        successToast();
      } else {
        const clientMessage =
          error &&
          typeof error === 'object' &&
          'clientMessage' in error &&
          typeof (error as { clientMessage?: unknown }).clientMessage === 'string'
            ? (error as { clientMessage: string }).clientMessage
            : undefined;
        toast.error(clientMessage || 'An error occurred');
      }
    } finally {
      isLoading.value = false;
    }
  };

  const deleteEmailSender = () => {
    if (isLoading.value) return;
    isLoading.value = true;
    confirmDeleteDialogOpen.value = false;

    stuut.emailSenders
      .delete(props.emailSender.id)
      .then(() => {
        toast.success(`${props.emailSender.email} was unlinked.`);
        emits('deleted');
      })
      .catch((error) => {
        toast.error(error.clientMessage || 'An error occurred');
      })
      .finally(() => (isLoading.value = false));
  };
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button size="icon" variant="ghost" :disabled="isLoading">
        <i class="fa far fa-ellipsis-h" />
      </Button>
    </DropdownMenuTrigger>

    <DropdownMenuContent class="w-56">
      <DropdownMenuGroup>
        <DropdownMenuItem
          v-if="emailSender.id !== profileStore.profile.default_email_sender_id"
          icon
          @click="setDefaultSender"
        >
          <p>Make Primary</p>
          <i class="fa far fa-star" />
        </DropdownMenuItem>

        <DropdownMenuItem icon @click="emits('editSignature')">
          <p>Edit Signature</p>
          <i class="fa far fa-signature" />
        </DropdownMenuItem>

        <DropdownMenuItem
          v-if="!emailSender.is_centralized_inbox"
          icon
          :disabled="emailSender.user_id !== profileStore.profile.id"
          @click="setShared"
        >
          <p>{{ emailSender.is_shared ? 'Hide From' : 'Share With' }} Org</p>
          <i class="fa far fa-globe" />
        </DropdownMenuItem>

        <DropdownMenuItem
          icon
          :disabled="emailSender.user_id !== profileStore.profile.id"
          @click="toggleCentralizedInbox"
        >
          <p>{{ emailSender.is_centralized_inbox ? 'Disable' : 'Enable' }} Global Inbox</p>
          <i class="fa far fa-inbox" />
        </DropdownMenuItem>

        <!--        Disable for now until we have email sender archiving working -->
        <!--        <DropdownMenuItem -->
        <!--          v-if="emailSender.user_id === profileStore.profile.id" -->
        <!--          icon -->
        <!--          @click="confirmDeleteDialogOpen = true" -->
        <!--        > -->
        <!--          <p>Unlink Email</p> -->
        <!--          <i class="fa far fa-link-slash" /> -->
        <!--        </DropdownMenuItem> -->
      </DropdownMenuGroup>
    </DropdownMenuContent>

    <ConfirmDialog
      v-model="confirmDeleteDialogOpen"
      confirm-text="Unlink"
      @confirm="deleteEmailSender"
    >
      <template #title> Are you sure? </template>

      <template #description>
        This will permanently unlink this email and you will not be able to use it until you relink.
      </template>
    </ConfirmDialog>
  </DropdownMenu>
</template>

<style scoped></style>
